/** Shared, deliberately restrained shader constants for the river material. */
export const WATER_RENDER_CONFIG = Object.freeze({
  rippleHeightM: 0.025,
  flowSpeed: 0.38,
  secondaryFlowSpeed: 0.27,
  shorelineStart01: 0.72,
  wetEdgeStart01: 0.94,
  shorelineColourMix: 0.28,
  skyReflectionStrength: 0.52,
  daylightGlintStrength: 0.018,
  nightBrightnessScale: 0.5,
});

/**
 * Source-backed Neris bank constraints.
 *
 * GRPK supplies the river as a centreline rather than a surveyed shoreline.
 * These checked-in OSM ways have at least one long, parallel riverside run in
 * the generated world. Keeping their stable source IDs here also constrains a
 * short fragment of the same way at a tile boundary. Per-section distance and
 * alignment checks still prevent bridge-crossing parts from acting as banks.
 */
export const NERIS_BANK_PATH_SOURCE_IDS: ReadonlySet<string> = Object.freeze(
  new Set([
    "osm:way:4870008",
    "osm:way:4870048",
    "osm:way:1343690161",
    "osm:way:235121761",
    "osm:way:235121828",
    "osm:way:4871813",
    "osm:way:804868558",
    "osm:way:1091150540",
    "osm:way:1091150542",
    "osm:way:1312211524",
    "osm:way:1312211525",
    "osm:way:1312213309",
    "osm:way:198467942",
    "osm:way:198467943",
    "osm:way:198467944",
    "osm:way:198467945",
    "osm:way:235121763",
    "osm:way:235121834",
    "osm:way:235122416",
    "osm:way:245154338",
    "osm:way:360103209",
    "osm:way:42584424",
    "osm:way:439924550",
    "osm:way:439924551",
    "osm:way:553012420",
    "osm:way:842021920",
  ]),
);

/** Keep the inferred water edge clear of the mapped walking surface. */
export const NERIS_BANK_PATH_CLEARANCE_M = 2;

/** Ignore a bank segment if it no longer runs broadly with the river. */
export const NERIS_BANK_PATH_MINIMUM_ALIGNMENT = 0.6;

/** A clipped tile segment may still constrain nearby water sections. */
export const NERIS_BANK_PATH_MAXIMUM_DISTANCE_M = 60;

/** Prevent a noisy or crossing source segment from collapsing the river. */
export const NERIS_BANK_MINIMUM_HALF_WIDTH_M = 18;

/** Default river-terrain refinement outside mapped Neris bank corridors. */
export const WATER_TERRAIN_MAXIMUM_SPACING_M = 4;

/**
 * Mapped Neris banks need a finer heightfield so their visible contour
 * does not resolve as four-metre diagonal teeth.
 */
export const NERIS_BANK_TERRAIN_MAXIMUM_SPACING_M = 2;

/**
 * Blend the river bed up to the source terrain before the water edge. Keeping
 * this transition inside the water footprint preserves adjacent dry paths.
 */
export const WATER_TERRAIN_BANK_TRANSITION_M = 4;
